import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineOutletAdmin, DineOutletTaxProfiles } from 'api/types';

/**
 * An outlet's tax profile (DineOps-Tax-Design T1, T10, T11). Every bill line of the outlet is
 * restaurant service under it — one SAC and one GST rate — and its prices either include the tax
 * or have it added. A change is a new row from a date (today or later); the current row ends the
 * day before, so bills already issued keep what they were taxed with. Adding a row needs the
 * Outlets screen's 'tax' action; the server checks it. With tax on and no row in force, the
 * outlet's bills are refused.
 */
interface Props {
  isOpen: boolean;
  outlet: DineOutletAdmin | null;
  onClose: () => void;
  /**
   * Shown as a section of the outlet window (DineOpsOutletWorkspace) rather than as its own window:
   * no backdrop, header or close; saving stays in the section. Default: its own window.
   */
  embedded?: boolean;
  /** Embedded: unsaved edits in this section, so the outlet window can warn before closing. */
  onDirtyChange?: (dirty: boolean) => void;
  /** After a row is saved, so the outlet list's Tax column can refresh. */
  onSaved?: () => void;
}

interface FormState {
  effectiveFrom: string;
  serviceSacCode: string;
  serviceRateCode: string;
  pricesIncludeTax: boolean;
  remarks: string;
}

const fmt = (v: string | null) =>
  v ? new Date(v).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const DineOpsOutletTaxProfileModal: React.FC<Props> = ({ isOpen, outlet, onClose, embedded = false, onDirtyChange, onSaved }) => {
  const [data, setData] = useState<DineOutletTaxProfiles | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const load = useCallback(async () => {
    if (!outlet) return;
    setLoading(true);
    try {
      setData(await dineOpsService.getOutletTaxProfiles(outlet.id));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'The tax profile could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [outlet]);

  useEffect(() => {
    if (!isOpen) return;
    setForm(null);
    setError(null);
    void load();
  }, [isOpen, load]);

  useEffect(() => { onDirtyChange?.(!!form); }, [form, onDirtyChange]);

  const current = data?.rows.find((r) => r.isCurrent) ?? null;
  const rateOptions = useMemo(
    () => (data?.rateCodes ?? []).map((r) => ({ value: r.rateCode, label: `${r.rateCode} — ${r.totalRate}%` })),
    [data],
  );

  const openForm = () => {
    setError(null);
    setForm({
      effectiveFrom: (data?.businessDate ?? new Date().toISOString()).slice(0, 10),
      serviceSacCode: current?.serviceSacCode ?? '996331',
      serviceRateCode: current?.serviceRateCode ?? '',
      pricesIncludeTax: current?.pricesIncludeTax ?? false,
      remarks: '',
    });
  };

  const formError = useMemo(() => {
    if (!form) return null;
    if (!/^[0-9]{4,8}$/.test(form.serviceSacCode.trim())) return 'The SAC is 4 to 8 digits, e.g. 996331 for restaurant service.';
    if (!form.serviceRateCode) return 'Choose the GST rate for restaurant service.';
    if (data && form.effectiveFrom < data.businessDate.slice(0, 10)) return `Start on or after ${fmt(data.businessDate)}: bills already issued keep their tax.`;
    return null;
  }, [form, data]);

  const handleClose = () => (form ? setDiscardOpen(true) : onClose());

  const save = async () => {
    if (!outlet || !form) return;
    setConfirmOpen(false);
    setSaving(true);
    setError(null);
    try {
      setData(await dineOpsService.addOutletTaxProfile(outlet.id, {
        effectiveFrom: form.effectiveFrom, serviceSacCode: form.serviceSacCode.trim(), serviceRateCode: form.serviceRateCode,
        pricesIncludeTax: form.pricesIncludeTax, remarks: form.remarks.trim() || null,
      }));
      notificationService.toast.success(`Tax profile for "${outlet.outletName}" saved from ${fmt(form.effectiveFrom)}.`);
      setForm(null);
      onSaved?.();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The tax profile could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !outlet) return null;
  const cell: React.CSSProperties = { padding: '6px 10px', borderBottom: '1px solid var(--border-color)', fontSize: 13, verticalAlign: 'top' };

  const overlayEl = (saving || loading) && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{saving ? 'Saving...' : 'Loading...'}</span>
          </div>
        );
  const body = (
    <>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
          Food and drink served here is taxed as <strong>restaurant service</strong>: one SAC and one GST rate for the bill,
          whatever the dish. Choose whether the outlet's prices (menu, price lists, buffet rates) <strong>include</strong> the
          tax or have it added. The rate (5 % or 18 % for a specified premises) is the property's decision with its CA.
        </p>
        {!current && !loading && (
          <div className="form-error-message" style={{ color: 'var(--warning-text, #92400e)', marginBottom: 10 }}>
            No tax profile yet: while tax is on, this outlet cannot generate a bill. Add one below.
          </div>
        )}

        <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} data-tax-profiles>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                <th style={cell}>From</th><th style={cell}>To</th><th style={cell}>SAC</th><th style={cell}>Rate</th>
                <th style={cell}>Prices</th><th style={cell}>Remarks</th><th style={cell}>Set by</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).length === 0 && !loading && (
                <tr><td style={{ ...cell, color: 'var(--text-muted)' }} colSpan={7}>No rows.</td></tr>
              )}
              {(data?.rows ?? []).map((r) => (
                <tr key={r.id} style={r.isCurrent ? { fontWeight: 600 } : undefined}>
                  <td style={cell}>{fmt(r.effectiveFrom)}{r.isCurrent ? ' · current' : ''}</td>
                  <td style={cell}>{fmt(r.effectiveTo)}</td>
                  <td style={cell}>{r.serviceSacCode}</td>
                  <td style={cell}>{r.serviceRateCode}</td>
                  <td style={cell}>{r.pricesIncludeTax ? 'Include tax' : 'Before tax'}</td>
                  <td style={{ ...cell, color: 'var(--text-secondary)' }}>{r.remarks ?? ''}</td>
                  <td style={{ ...cell, color: 'var(--text-secondary)' }}>{r.createdByName ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data?.canEdit && !form && (
          <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={openForm}>
            <IconSystem.add size={16} /> {current ? 'Change from a date' : 'Set tax profile'}
          </button>
        )}
        {data && !data.canEdit && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
            Changing it needs the Outlets screen's "tax" permission (Role Permissions).
          </p>
        )}

        {form && (
          <div style={{ marginTop: 16, padding: 12, border: '1px solid var(--border-color)', borderRadius: 6 }} data-tax-profile-form>
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">From date <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={form.effectiveFrom} min={data?.businessDate.slice(0, 10)}
                  onChange={(e) => setForm((f) => f && { ...f, effectiveFrom: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Restaurant service SAC <span className="text-danger">*</span></label>
                <input type="text" className="form-input" value={form.serviceSacCode} maxLength={8}
                  onChange={(e) => setForm((f) => f && { ...f, serviceSacCode: e.target.value.replace(/\D/g, '') })} />
              </div>
              <div className="form-group">
                <SyutSelect label="GST rate" required options={rateOptions} value={form.serviceRateCode} isClearable={false}
                  size="md" menuPortalTarget={document.body} placeholder="Choose the rate..."
                  onChange={(v) => setForm((f) => f && { ...f, serviceRateCode: (v as string) || '' })} />
              </div>
            </div>
            <div className="form-grid-2">
              <div className="form-group">
                <SyutSelect label="Prices" required size="md" isClearable={false} menuPortalTarget={document.body}
                  options={[{ value: 'false', label: 'Before tax — tax is added on the bill' }, { value: 'true', label: 'Include tax — tax is inside the price' }]}
                  value={String(form.pricesIncludeTax)}
                  onChange={(v) => setForm((f) => f && { ...f, pricesIncludeTax: v === 'true' })} />
              </div>
              <div className="form-group">
                <label className="form-label">Remarks</label>
                <input type="text" className="form-input" value={form.remarks} maxLength={500}
                  placeholder="e.g. Specified premises from FY 2026-27 per CA"
                  onChange={(e) => setForm((f) => f && { ...f, remarks: e.target.value })} />
              </div>
            </div>
            {(formError || error) && <div className="form-error-message" style={{ marginBottom: 8 }}>{formError ?? error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setForm(null); setError(null); }} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => setConfirmOpen(true)} disabled={saving || !!formError}>Save</button>
            </div>
          </div>
        )}
    </>
  );
  const dialogs = (
    <>
    <ConfirmDialog open={confirmOpen} title="Confirm Tax Profile"
      message={form ? `From ${fmt(form.effectiveFrom)}, "${outlet.outletName}" bills restaurant service under SAC ${form.serviceSacCode} at ${form.serviceRateCode}, with prices ${form.pricesIncludeTax ? 'including' : 'before'} tax.${current ? ' The current profile ends the day before.' : ''}` : ''}
      confirmLabel="Save" cancelLabel="Cancel" variant="default" onConfirm={() => void save()} onCancel={() => setConfirmOpen(false)} />
    <ConfirmDialog open={discardOpen} title="Discard Changes?" message="You have an unsaved change. Do you want to discard it?"
      confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
      onConfirm={() => { setDiscardOpen(false); setForm(null); onClose(); }} onCancel={() => setDiscardOpen(false)} />
    </>
  );

  if (embedded) {
    return (
      <div className="outlet-ws-pane">
        {overlayEl}
        {body}
        {dialogs}
      </div>
    );
  }

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {overlayEl}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Tax Profile — {outlet.outletName} ({outlet.outletCode})</h2>
          <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {body}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Close</button>
        </div>
      </div>
      {dialogs}
    </>
  );
};

export default DineOpsOutletTaxProfileModal;
